import * as XLSX from "xlsx";

export function exportToExcel(rows, filename = "export.xlsx", sheet = "Sheet1") {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheet);
  XLSX.writeFile(wb, filename);
}

export function exportAoa(aoa, filename, sheet = "Sheet1", { cols = [], merges = [] } = {}) {
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = cols.map((wch) => ({ wch }));
  ws["!merges"] = merges;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheet);
  XLSX.writeFile(wb, filename);
}

export function importFromExcel(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws);
        if (json.length > 5000) {
          reject(new Error(`File has ${json.length} rows. Please split into batches of ≤ 5000 rows.`));
          return;
        }
        resolve(json);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

// Reads every sheet as a raw grid (array of arrays) so any layout can be mapped.
export function readWorkbookRaw(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array", cellDates: true });
        const sheets = wb.SheetNames.map((name) => ({
          name,
          rows: XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, defval: "", blankrows: false, raw: true }),
        }));
        resolve(sheets);
      } catch (err) { reject(err); }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

const norm = (s) => String(s ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

// Picks the row (within the first 30) that best looks like a header row.
export function detectHeaderRow(rows, synonyms) {
  const all = new Set(Object.values(synonyms).flat().map(norm));
  let best = 0, bestScore = -1;
  rows.slice(0, 30).forEach((r, i) => {
    const cells = r.filter((c) => typeof c === "string" && c.trim());
    const hits = r.filter((c) => all.has(norm(c))).length;
    const score = hits * 3 + cells.length;
    if (cells.length >= 2 && score > bestScore) { best = i; bestScore = score; }
  });
  return best;
}

export function autoMap(headers, synonyms) {
  const map = {};
  const used = new Set();
  const hn = headers.map(norm);
  for (const [field, syns] of Object.entries(synonyms)) {
    const ns = syns.map(norm);
    const idx = hn.findIndex((h, i) => !used.has(i) && h && ns.includes(h));
    if (idx >= 0) { map[field] = idx; used.add(idx); }
  }
  for (const [field, syns] of Object.entries(synonyms)) {
    if (map[field] !== undefined) continue;
    const ns = syns.map(norm);
    const idx = hn.findIndex((h, i) => !used.has(i) && h && ns.some((s) => s.length > 3 && (h.includes(s) || s.includes(h))));
    if (idx >= 0) { map[field] = idx; used.add(idx); }
  }
  return map;
}

export function toISODate(v) {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v) ? null : v.toISOString();
  if (typeof v === "number") return new Date(Math.round((v - 25569) * 86400000)).toISOString();
  const s = String(v).trim();
  const m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/);
  if (m) {
    const y = m[3].length === 2 ? `20${m[3]}` : m[3];
    const d = new Date(Date.UTC(+y, +m[2] - 1, +m[1]));
    return isNaN(d) ? null : d.toISOString();
  }
  const d = new Date(s);
  return isNaN(d) ? null : d.toISOString();
}
